import React, { useState } from 'react';
import './ManualScraping.css';
import { API_CONFIG } from '../../config/api';

const ManualScraping: React.FC = () => {
  const [dailyScrapingLoading, setDailyScrapingLoading] = useState(false);
  const [dailyScrapingProgress, setDailyScrapingProgress] = useState<string[]>([]);
  const [lastDailyRun, setLastDailyRun] = useState<string | null>(null);

  const [aiSolutionsLoading, setAiSolutionsLoading] = useState(false);
  const [aiSolutionsProgress, setAiSolutionsProgress] = useState<string[]>([]);
  const [lastAiSolutionsRun, setLastAiSolutionsRun] = useState<string | null>(null);

  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklyProgress, setWeeklyProgress] = useState<string[]>([]);
  const [lastWeeklyRun, setLastWeeklyRun] = useState<string | null>(null);

  const handleDailyScrape = async () => {
    setDailyScrapingLoading(true);
    setDailyScrapingProgress(['🚀 Starting daily news scraper...']);
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/run-scraper`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to start daily scraper');
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n').filter(line => line.trim());

          for (const line of lines) {
            try {
              const data = JSON.parse(line);
              
              if (data.type === 'progress') {
                setDailyScrapingProgress(prev => [...prev, `📰 ${data.message}`]);
              } else if (data.type === 'complete') {
                setLastDailyRun(new Date().toLocaleString());
                setDailyScrapingProgress(prev => [...prev, '✅ Daily news scraping completed! Articles are now live.']);
              } else if (data.type === 'error') {
                setDailyScrapingProgress(prev => [...prev, `❌ ${data.message}`]);
              }
            } catch (e) {
              // Ignore JSON parse errors for partial chunks
            }
          }
        }
      }
    } catch (error) {
      console.error('Error starting daily scrape:', error);
      setDailyScrapingProgress(prev => [...prev, '❌ Error starting daily news scraper']);
    } finally {
      setDailyScrapingLoading(false);
    }
  };

  const handleAiSolutionsScrape = async () => {
    setAiSolutionsLoading(true);
    setAiSolutionsProgress(['🚀 Starting AI solutions discovery...']);
    
    try {
      // Simulate progress updates (since we can't get real-time updates from the backend yet)
      const progressSteps = [
        '🧠 Generating AI-powered search queries...',
        '🔍 Searching GitHub for new AI repositories...',
        '🌐 Searching Product Hunt for AI tools...',
        '📰 Searching Hacker News for AI discussions...',
        '🤖 AI analyzing and curating discovered solutions...',
        '📊 Categorizing and tagging solutions...',
        '💾 Saving pending solutions to database...'
      ];

      for (let i = 0; i < progressSteps.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        setAiSolutionsProgress(prev => [...prev, progressSteps[i]]);
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/scrape/ai-solutions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (data.success) {
        setLastAiSolutionsRun(new Date().toLocaleString());
        setAiSolutionsProgress(prev => [...prev, '✅ AI solutions discovery completed! Check AI Solutions tab for pending approvals.']);
      } else {
        setAiSolutionsProgress(prev => [...prev, '❌ AI solutions discovery failed']);
      }
    } catch (error) {
      console.error('Error starting AI solutions scrape:', error);
      setAiSolutionsProgress(prev => [...prev, '❌ Error starting AI solutions discovery']);
    } finally {
      setAiSolutionsLoading(false);
    }
  };

  const handleWeeklySummary = async () => {
    setWeeklyLoading(true);
    setWeeklyProgress(['📊 Starting weekly summary generation...']);
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/generate-weekly-summary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to start weekly summary generation');
      }

      const result = await response.json();
      
      setWeeklyProgress(prev => [...prev, 
        '✅ Weekly summary generated successfully',
        `📝 Summary: ${result.summary?.substring(0, 100)}...`,
        `🔗 Linked ${result.dailySummariesCount} daily summaries`
      ]);
      
      setLastWeeklyRun(new Date().toLocaleString());
      
    } catch (error) {
      console.error('Weekly summary error:', error);
      setWeeklyProgress(prev => [...prev, `❌ Error: ${error}`]);
    } finally {
      setWeeklyLoading(false);
    }
  };

  return (
    <div className="manual-scraping">
      <h2>Manual Scraping Controls</h2>
      <p className="manual-scraping__description">
        Run scraping processes manually outside of their normal schedules.
      </p>

      <div className="scraping-cards">
        <div className="scraping-card">
          <div className="scraping-card__header">
            <h3>Daily News Scraper</h3>
            <span className="schedule-info">Normally runs daily at 3 AM PST</span>
          </div>
          
          <div className="scraping-card__content">
            <p>
              Scrapes latest business and AI news from configured RSS sources. 
              Articles are automatically approved and go live immediately.
            </p>
            
            <div className="discovery-features">
              <h4>Process:</h4>
              <ul>
                <li>📰 Scrapes RSS feeds from news sources</li>
                <li>🔍 Extracts article information</li>
                <li>🚫 Removes duplicates</li>
                <li>✅ Auto-approves and publishes articles</li>
                <li>📊 Creates daily summary</li>
              </ul>
            </div>
            
            {lastDailyRun && (
              <p className="last-run">
                Last manual run: <strong>{lastDailyRun}</strong>
              </p>
            )}
          </div>
          
          <div className="scraping-card__actions">
            <button
              className="scrape-btn daily-scrape-btn"
              onClick={handleDailyScrape}
              disabled={dailyScrapingLoading}
            >
              {dailyScrapingLoading ? 'Running Daily Scraper...' : 'Run Daily News Scraper'}
            </button>
          </div>
        </div>

        <div className="scraping-card">
          <div className="scraping-card__header">
            <h3>AI Solutions Discovery</h3>
            <span className="schedule-info">Normally runs weekly on Sunday 6 PM PST</span>
          </div>
          
          <div className="scraping-card__content">
            <p>
              Intelligent discovery of new AI tools and solutions using AI-powered search and curation.
              Results require admin approval before publishing.
            </p>
            
            <div className="discovery-features">
              <h4>Discovery Process:</h4>
              <ul>
                <li>🧠 AI generates diverse search queries</li>
                <li>🔍 Multi-source search (GitHub, Product Hunt, Hacker News)</li>
                <li>🤖 AI curation and validation</li>
                <li>📊 Automatic categorization and tagging</li>
                <li>👨💼 Admin approval workflow</li>
              </ul>
            </div>
            
            {lastAiSolutionsRun && (
              <p className="last-run">
                Last manual run: <strong>{lastAiSolutionsRun}</strong>
              </p>
            )}
          </div>
          
          <div className="scraping-card__actions">
            <button
              className="scrape-btn ai-solutions-btn"
              onClick={handleAiSolutionsScrape}
              disabled={aiSolutionsLoading}
            >
              {aiSolutionsLoading ? 'Discovering...' : 'Run AI Solutions Discovery'}
            </button>
          </div>
        </div>

        <div className="scraping-card">
          <div className="scraping-card__header">
            <h3>Weekly Summary Generator</h3>
            <span className="schedule-info">Normally runs Sunday at 5 PM PST</span>
          </div>
          
          <div className="scraping-card__content">
            <p>
              Generates AI-powered weekly summaries from the past week's daily summaries.
              Creates comprehensive 200-300 word business insights.
            </p>
            
            <div className="discovery-features">
              <h4>Process:</h4>
              <ul>
                <li>📊 Analyzes past week's daily summaries</li>
                <li>🤖 Uses AI to identify key themes</li>
                <li>📝 Generates comprehensive weekly digest</li>
                <li>🔗 Links all daily summaries automatically</li>
              </ul>
            </div>
            
            {lastWeeklyRun && (
              <p className="last-run">Last run: {lastWeeklyRun}</p>
            )}
          </div>
          
          <div className="scraping-card__actions">
            <button 
              onClick={handleWeeklySummary}
              disabled={weeklyLoading}
              className="scraping-button"
            >
              {weeklyLoading ? 'Generating...' : 'Generate Weekly Summary'}
            </button>
          </div>
        </div>
      </div>

      {(dailyScrapingProgress.length > 0 || aiSolutionsProgress.length > 0 || weeklyProgress.length > 0) && (
        <div className="progress-section">
          <h3>Scraping Progress</h3>
          
          {dailyScrapingProgress.length > 0 && (
            <div className="progress-log">
              <h4>Daily News Scraper:</h4>
              {dailyScrapingProgress.map((step, index) => (
                <div key={index} className="progress-step">
                  <span className="progress-timestamp">
                    {new Date().toLocaleTimeString()}
                  </span>
                  <span className="progress-message">{step}</span>
                </div>
              ))}
            </div>
          )}

          {aiSolutionsProgress.length > 0 && (
            <div className="progress-log">
              <h4>AI Solutions Discovery:</h4>
              {aiSolutionsProgress.map((step, index) => (
                <div key={index} className="progress-step">
                  <span className="progress-timestamp">
                    {new Date().toLocaleTimeString()}
                  </span>
                  <span className="progress-message">{step}</span>
                </div>
              ))}
            </div>
          )}

          {weeklyProgress.length > 0 && (
            <div className="progress-log">
              <h4>Weekly Summary Generator:</h4>
              {weeklyProgress.map((step, index) => (
                <div key={index} className="progress-step">
                  <span className="progress-timestamp">
                    {new Date().toLocaleTimeString()}
                  </span>
                  <span className="progress-message">{step}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="scraping-notes">
        <h4>Important Notes:</h4>
        <ul>
          <li>Daily news scraping runs via AWS Lambda and auto-approves articles</li>
          <li>AI solutions discovery can take 10-15 minutes due to AI processing</li>
          <li>All discovered AI solutions require admin approval before going live</li>
          <li>Running scrapers manually won't affect their automatic schedules</li>
          <li>Use the "Scraped Articles Manager" to edit any published articles</li>
        </ul>
      </div>
    </div>
  );
};

export default ManualScraping;
